/** Chat models exposed in the model picker, one slot per provider. */
export const MODEL_IDS = [
  "openai/gpt-6.1-sol",
  "mistral/mistral-large-4",
  "anthropic/claude-sonnet-5-5",
] as const;

/** Chat models exposed in the model picker. Subset of `LLMModels`. */
export type ModelId = (typeof MODEL_IDS)[number];

/**
 * Where a retired slot went, for readers that hold an id of their own.
 *
 * The database is migrated when a slot moves; a browser is not. Someone who
 * had explicitly picked the previous generation still carries that id in
 * localStorage, and falling back to `auto` would quietly move them onto the
 * team default — a different provider. Following the successor keeps the
 * choice they actually made.
 *
 * Mirrors the `swaps` of the matching migration; the two move together.
 */
export const RETIRED_MODEL_SUCCESSORS = {
  "openai/gpt-5.4": "openai/gpt-6.1-sol",
  "openai/gpt-6-sol": "openai/gpt-6.1-sol",
  "anthropic/claude-sonnet-4-6": "anthropic/claude-sonnet-5-5",
  "anthropic/claude-sonnet-5": "anthropic/claude-sonnet-5-5",
  "mistral/mistral-medium-3-5": "mistral/mistral-large-4",
} as const satisfies Record<string, ModelId>;

/**
 * Date until which a chat model shows the "new" badge, per model.
 *
 * Read by the UI model card. The API cannot import it — it is a runtime value,
 * and this package's barrel re-exports a directory, which Node refuses to
 * resolve at boot (`ERR_UNSUPPORTED_DIR_IMPORT`). `LLMChatSupportedList`
 * therefore repeats the dates as literals, and `llm-supported.spec.ts` pins the
 * two copies together.
 *
 * A model absent from this map is simply never badged as new.
 */
export const MODEL_NEW_UNTIL: Partial<Record<ModelId, string>> = {
  // Deliberately empty: a generation switch is silent maintenance, so no model
  // is badged as new. Add an entry here (and mirror the date in
  // `LLMChatSupportedList`) only for a change the team actually wants to
  // announce.
};
